import type { DateRange } from 'react-day-picker'
import type { FieldViewProps } from '../../types'
import type DateController from './Controller'

import React from 'react'
import { format, isValid, startOfDay } from 'date-fns'
import { enUS as defaultLocale } from 'date-fns/locale/en-US'

import {
	DatePicker,
	DatePickerCalendar,
	DatePickerContent,
	DatePickerInput,
} from '@/shared/components/ui/date-picker'

import Combobox from '../../components/Combobox'
import ReferencePicker from '../../components/ReferencePicker'
import ReferenceWrapper from '../../components/ReferenceWrapper'
import { Operators } from '../../constants'
import { toOperationValueOption } from '../../utils/utils'
import { useFilterField } from '../useFilterField'

type Props = FieldViewProps<DateController>

export default function DateView({
	field,
	dateFormat,
	locale,
	onChange,
	storedValue,
	invalidMessage,
	disabled,
	fields,
	enableReferencing,
}: Props & { dateFormat: 'EU' | 'US' }) {
	const filterField = useFilterField<DateController>(field, {
		initialState: storedValue,
		onChange,
	})
	const mode = filterField.getFilterMode()
	const value = filterField.getFilterValue()
	const handleDateChange = (date: Date) =>
		filterField.setFilterValue(format(startOfDay(date), 'yyyy-MM-dd'))
	const handleDateRangeChange = (range?: DateRange) => {
		if (!range) {
			filterField.setFilterValue([])
		} else {
			filterField.setFilterValue(
				[range.from, range.to]
					.filter((date) => !!date && isValid(date))
					.map((date) => format(date!, 'yyyy-MM-dd')) as [string, string]
			)
		}
	}

	const { operation } = storedValue
	const isBetween = operation === Operators.DATE_BETWEEN
	const filterTypes = field
		.getFilterTypes()
		.map((t) => ({ ...t, label: locale.getOperatorLabel(t.operation) }))
	const operators = filterTypes.map(toOperationValueOption)!
	const selectedTilterType = filterTypes.find((t) => t.operation === operation)!
	const isInvalid = Boolean(invalidMessage)

	return (
		<>
			<div className='flex items-center'>
				<Combobox
					locale={locale}
					data-testid='query-builder-operation-select'
					name='operation'
					placeholder={locale.getOperationPlaceholder()}
					className='w-full'
					initialOpen={!operation}
					invalid={isInvalid}
					value={operation}
					onChange={filterField.setFilterOperation}
					options={operators}
				/>
			</div>
			<div className='col-span-2 flex items-center'>
				{selectedTilterType?.hasInput ? (
					isBetween ? (
						<DatePicker
							key='range'
							required
							mode='range'
							value={value as DateRange | undefined}
							onValueChange={handleDateRangeChange}
							invalid={isInvalid}
							disabled={disabled}
						>
							<DatePickerInput
								format={dateFormat}
								data-testid='query-builder-value-date'
							/>
							<DatePickerContent>
								<DatePickerCalendar
									numberOfMonths={2}
									locale={locale.getDateFnsLocalization?.() ?? defaultLocale}
									captionLayout='dropdown'
								/>
							</DatePickerContent>
						</DatePicker>
					) : enableReferencing ? (
						<ReferenceWrapper filterField={filterField} locale={locale}>
							{mode === 'reference' ? (
								<ReferencePicker
									locale={locale}
									filterField={filterField}
									fields={fields}
									isInvalid={isInvalid}
								/>
							) : (
								<DatePicker
									key='single'
									required
									mode='single'
									value={value as Date | undefined}
									onValueChange={handleDateChange}
									invalid={isInvalid}
									disabled={disabled}
								>
									<DatePickerInput
										format={dateFormat}
										className='w-full rounded-none first:rounded-s-lg last:-me-0 last:rounded-e-lg'
										data-testid='query-builder-value-date'
									/>
									<DatePickerContent>
										<DatePickerCalendar
											locale={
												locale.getDateFnsLocalization?.() ?? defaultLocale
											}
											captionLayout='dropdown'
										/>
									</DatePickerContent>
								</DatePicker>
							)}
						</ReferenceWrapper>
					) : (
						<DatePicker
							key='single'
							required
							mode='single'
							value={value as Date | undefined}
							onValueChange={handleDateChange}
							invalid={isInvalid}
							disabled={disabled}
						>
							<DatePickerInput
								format={dateFormat}
								data-testid='query-builder-value-date'
							/>
							<DatePickerContent>
								<DatePickerCalendar
									locale={locale.getDateFnsLocalization?.() ?? defaultLocale}
									captionLayout='dropdown'
								/>
							</DatePickerContent>
						</DatePicker>
					)
				) : null}
			</div>
		</>
	)
}
